import { pageFileName } from './prescriptionDocument.ts'

/**
 * Saves page images to the pharmacist's machine, one file per page (#494).
 *
 * From the bytes the viewer already holds as blob URLs: the image routes are
 * behind the session, so a plain link to them would download a 401.
 */

export interface DownloadHost {
  /** The content type behind a URL, or undefined when it cannot be read. */
  readonly contentTypeOf: (url: string) => Promise<string | undefined>
  /** Hands one file to the browser's download manager. */
  readonly save: (url: string, filename: string) => void
}

const browserHost: DownloadHost = {
  contentTypeOf: async (url) => {
    try {
      return (await (await fetch(url)).blob()).type
    } catch {
      return undefined
    }
  },
  save: (url, filename) => {
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
  },
}

/** Downloads `urls` as `<base>-p1.<ext>`, `<base>-p2.<ext>`... and returns the names used. */
export async function downloadPages(
  urls: readonly string[],
  base: string | undefined,
  host: DownloadHost = browserHost,
): Promise<readonly string[]> {
  const names: string[] = []
  for (const [position, url] of urls.entries()) {
    const name = pageFileName(base, position, (await host.contentTypeOf(url)) ?? '')
    host.save(url, name)
    names.push(name)
  }
  return names
}
